import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    EyeIcon,
    BellAlertIcon,
} from "@heroicons/react/20/solid";
import { Heading, Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS } from "../data/events";
import {
    StatCard,
    Card,
    RingChart,
    Sparkline,
    FeedEntry,
    CorrelationBadges,
    IncidentChain,
    SectionTitle,
    RingChartNext,
} from "../shared";

/* ── Email Flow Sankey ────────────────────────────────────────────── */
export function EmailFlowSankey() {
    return (
        <svg viewBox="0 0 1000 320" className="w-full">
            <defs>
                {/* Gradients for different "Threat Levels" */}
                <linearGradient id="gradClean" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0.1" />
                </linearGradient>
                <linearGradient id="gradUnwanted" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0.1" />
                </linearGradient>
                <linearGradient id="gradDanger" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#b91c1c" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="gradSpam" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#f97316" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ea580c" stopOpacity="0.2" />
                </linearGradient>
            </defs>

            {/* --- PATHS: Stage 1 to 2 (The Big Split) --- */}
            {/* Transmitted (The bulk of traffic) */}
            <path d="M100 80 C200 80, 250 50, 400 50" fill="none" stroke="url(#gradClean)" strokeWidth="80" />
            {/* Unwanted Messages (The 70 from your screenshot) */}
            <path d="M100 140 C200 140, 250 200, 350 200" fill="none" stroke="url(#gradUnwanted)" strokeWidth="30" />

            {/* --- PATHS: Stage 2 to 3 (The Categories) --- */}
            {/* From Unwanted -> Benign (57) */}
            <path d="M350 190 C420 190, 480 160, 550 160" fill="none" stroke="#a855f7" strokeWidth="20" opacity="0.2" />
            {/* From Unwanted -> Malicious (10) */}
            <path d="M350 205 C420 205, 480 220, 550 220" fill="none" stroke="#ef4444" strokeWidth="10" opacity="0.2" />
            {/* From Unwanted -> Spam (2) */}
            <path d="M350 215 C420 215, 480 260, 550 260" fill="none" stroke="#f97316" strokeWidth="4" opacity="0.2" />
            {/* From Unwanted -> Graymail (1) */}
            <path d="M350 220 C420 220, 480 180, 550 180" fill="none" stroke="#64748b" strokeWidth="2" opacity="0.2" />

            {/* --- PATHS: Stage 3 to 4 (The Outcomes) --- */}
            {/* Benign (57) + Graymail (1) -> Unremediated (58) */}
            <path d="M650 160 C750 160, 800 160, 900 160" fill="none" stroke="#94a3b8" strokeWidth="22" opacity="0.1" />
            {/* Malicious (10) -> Quarantined (10) */}
            <path d="M650 220 C750 220, 800 220, 900 220" fill="none" stroke="#ef4444" strokeWidth="10" opacity="0.1" />
            {/* Spam (2) -> Move to Spam (2) */}
            <path d="M650 260 C750 260, 800 260, 900 260" fill="none" stroke="#f97316" strokeWidth="4" opacity="0.1" />

            {/* User Reported Path (Feedback loop) */}
            <path
                d="M500 50 C700 50, 800 100, 900 100"
                fill="none"
                stroke="#ef4444"
                strokeWidth="3"
                strokeDasharray="4 2"
            />

            {/* --- NODES & LABELS --- */}

            {/* Start: Inbound */}
            <rect
                x="20"
                y="60"
                width="80"
                height="120"
                rx="8"
                fill="rgba(59,130,246,0.05)"
                stroke="rgba(59,130,246,0.2)"
            />
            <text x="60" y="115" textAnchor="middle" className="fill-zinc-800 text-xs font-bold dark:fill-zinc-200">
                48,361
            </text>
            <text x="60" y="130" textAnchor="middle" className="fill-zinc-400 text-[8px] uppercase tracking-wider">
                Inbound
            </text>

            {/* Transmitted Node */}
            <rect
                x="400"
                y="10"
                width="100"
                height="80"
                rx="6"
                fill="rgba(34,197,94,0.05)"
                stroke="rgba(34,197,94,0.2)"
            />
            <text x="450" y="45" textAnchor="middle" className="fill-zinc-800 text-[10px] font-bold dark:fill-zinc-200">
                Transmitted
            </text>
            <text x="450" y="58" textAnchor="middle" className="fill-emerald-600 text-[8px] font-bold uppercase">
                Delivered
            </text>

            {/* Categories (The Breakdown) */}
            <g className="fill-zinc-600 text-[9px] font-medium dark:fill-zinc-400">
                <text x="555" y="163">
                    Benign (57)
                </text>
                <text x="555" y="183">
                    Graymail (1)
                </text>
                <text x="555" y="223" className="fill-red-600 font-bold">
                    Malicious (10)
                </text>
                <text x="555" y="263" className="fill-orange-600">
                    Spam (2)
                </text>
            </g>

            {/* Final Outcomes (Far Right) */}
            <g>
                <rect x="900" y="150" width="12" height="20" rx="2" fill="#94a3b8" />
                <text x="920" y="163" className="fill-zinc-800 text-[10px] font-bold dark:fill-zinc-200">
                    Unremediated (58)
                </text>

                <rect x="900" y="90" width="12" height="20" rx="2" fill="#ef4444" />
                <text x="920" y="103" className="fill-red-600 text-[10px] font-bold">
                    User Reported (5)
                </text>

                <rect x="900" y="210" width="12" height="20" rx="2" fill="#b91c1c" />
                <text x="920" y="223" className="fill-zinc-800 text-[10px] font-bold dark:fill-zinc-200">
                    Quarantined (10)
                </text>

                <rect x="900" y="250" width="12" height="20" rx="2" fill="#f97316" />
                <text x="920" y="263" className="fill-zinc-800 text-[10px] font-bold dark:fill-zinc-200">
                    Moved to Spam (2)
                </text>
            </g>
        </svg>
    );
}

/* ── Sender Reputation Board ──────────────────────────────────────── */
function TopTargets() {
    const securityData = [
        { name: "S. Miller", malicious: 14, flagged: 45 },
        { name: "A. Novak", malicious: 8, flagged: 32 },
        { name: "J. Doe", malicious: 21, flagged: 56 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
        { name: "K. Braun", malicious: 3, flagged: 19 },
    ];

    return (
        <Table className="mt-4" dense>
            <TableHead>
                <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Malicious Messages</TableHeader>
                    <TableHeader>Flagged Messages</TableHeader>
                </TableRow>
            </TableHead>
            <TableBody>
                {securityData.map((row) => (
                    <TableRow key={row.name}>
                        <TableCell className="font-medium text-zinc-900 dark:text-white">{row.name}</TableCell>
                        <TableCell className="font-mono font-bold text-red-600 dark:text-red-400">
                            {row.malicious}
                        </TableCell>
                        <TableCell className="font-mono text-zinc-600 dark:text-zinc-400">{row.flagged}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function TopSenderDomains() {
    const securityData = [
        { name: "gmail.com", malicious: 14, flagged: 45 },
        { name: "mail.ru", malicious: 8, flagged: 32 },
        { name: "4timing.it", malicious: 21, flagged: 56 },
        { name: "daum.net", malicious: 3, flagged: 19 },
    ];

    return (
        <Table className="mt-4" dense>
            <TableHead>
                <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Malicious Messages</TableHeader>
                    <TableHeader>Flagged Messages</TableHeader>
                </TableRow>
            </TableHead>
            <TableBody>
                {securityData.map((row) => (
                    <TableRow key={row.name}>
                        <TableCell className="font-medium text-zinc-900 dark:text-white">{row.name}</TableCell>
                        <TableCell className="font-mono font-bold text-red-600 dark:text-red-400">
                            {row.malicious}
                        </TableCell>
                        <TableCell className="font-mono text-zinc-600 dark:text-zinc-400">{row.flagged}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function TopAttackingSender() {
    const securityData = [
        { name: "mario@gmail.com", malicious: 14, flagged: 45 },
        { name: "luigi@mail.ru", malicious: 8, flagged: 32 },
        { name: "primabella@4timing.it", malicious: 21, flagged: 56 },
        { name: "glumanda@daum.net", malicious: 3, flagged: 19 },
    ];

    return (
        <Table className="mt-4" dense>
            <TableHead>
                <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Malicious Messages</TableHeader>
                    <TableHeader>Flagged Messages</TableHeader>
                </TableRow>
            </TableHead>
            <TableBody>
                {securityData.map((row) => (
                    <TableRow key={row.name}>
                        <TableCell className="font-medium text-zinc-900 dark:text-white">{row.name}</TableCell>
                        <TableCell className="font-mono font-bold text-red-600 dark:text-red-400">
                            {row.malicious}
                        </TableCell>
                        <TableCell className="font-mono text-zinc-600 dark:text-zinc-400">{row.flagged}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

/* ── Attachment Analysis (Donut) ──────────────────────────────────── */
function AttachmentAnalysis() {
    const types = [
        { label: "EXE", pct: 34, color: "#ef4444" },
        { label: "DOC", pct: 28, color: "#f97316" },
        { label: "PDF", pct: 18, color: "#eab308" },
        { label: "ZIP", pct: 12, color: "#3b82f6" },
        { label: "Other", pct: 8, color: "#6b7280" },
    ];
    let cum = 0;
    const total = types.reduce((s, t) => s + t.pct, 0);
    const gradientStops = types.flatMap((t) => {
        const start = (cum / total) * 360;
        cum += t.pct;
        const end = (cum / total) * 360;
        return [`${t.color} ${start}deg ${end}deg`];
    });
    return (
        <div className="flex flex-col items-center gap-4">
            <div
                className="relative rounded-full shadow-lg shadow-red-500/10"
                style={{ width: 130, height: 130, background: `conic-gradient(${gradientStops.join(", ")})` }}
            >
                <div
                    className="absolute inset-0 m-auto rounded-full bg-white/90 backdrop-blur-sm dark:bg-zinc-900/90"
                    style={{ width: 75, height: 75 }}
                />
                <span className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-extrabold text-zinc-700 dark:text-zinc-200">2,847</span>
                    <span className="text-[8px] text-zinc-400">Blocked</span>
                </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
                {types.map((t) => (
                    <div key={t.label} className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                        {t.label} ({t.pct}%)
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Phishing Keyword Cloud ───────────────────────────────────────── */
function PhishingKeywordCloud() {
    const words = [
        { text: "invoice", size: 22, x: 30, y: 30 },
        { text: "urgent", size: 20, x: 160, y: 25 },
        { text: "verify", size: 18, x: 290, y: 35 },
        { text: "password", size: 24, x: 80, y: 65 },
        { text: "account", size: 19, x: 230, y: 60 },
        { text: "suspended", size: 16, x: 370, y: 55 },
        { text: "click here", size: 21, x: 140, y: 95 },
        { text: "update", size: 15, x: 50, y: 100 },
        { text: "payment", size: 17, x: 300, y: 90 },
        { text: "delivery", size: 14, x: 400, y: 30 },
        { text: "security", size: 16, x: 420, y: 80 },
        { text: "action required", size: 18, x: 200, y: 120 },
        { text: "confirm", size: 13, x: 370, y: 115 },
        { text: "expire", size: 15, x: 70, y: 130 },
    ];
    return (
        <svg viewBox="0 0 500 150" className="w-full">
            {words.map((w) => (
                <text
                    key={w.text}
                    x={w.x}
                    y={w.y}
                    fontSize={w.size}
                    fontWeight={w.size > 18 ? "800" : "600"}
                    className="fill-blue-600/70 transition-all hover:fill-red-500 dark:fill-blue-400/70 dark:hover:fill-red-400"
                    style={{ cursor: "default" }}
                >
                    {w.text}
                </text>
            ))}
        </svg>
    );
}

/* ── Delivery Timeline (24h area with blocked overlay) ────────────── */
function DeliveryTimeline() {
    const delivered = [
        800, 500, 300, 200, 250, 400, 1200, 2800, 3200, 3400, 3100, 2900, 2700, 2500, 2800, 3000, 2600, 2200, 1800,
        1500, 1200, 1000, 900, 800,
    ];
    const blocked = [
        30, 20, 10, 8, 12, 18, 50, 120, 180, 200, 190, 170, 150, 130, 160, 180, 140, 110, 80, 60, 50, 40, 35, 30,
    ];
    const max = 3600;
    const w = 560,
        h = 80;

    const toPath = (data: number[]) =>
        data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * h}`).join(" ");
    const toArea = (data: number[]) => `M0,${h} L${toPath(data).split(" ").join(" L")} L${w},${h}Z`;

    return (
        <svg viewBox={`0 0 ${w} ${h + 20}`} className="w-full">
            <defs>
                <linearGradient id="delFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id="blkFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity="0.05" />
                </linearGradient>
            </defs>
            <path d={toArea(delivered)} fill="url(#delFill)" />
            <path
                d={`M${toPath(delivered).split(" ").join(" L")}`}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
            <path d={toArea(blocked)} fill="url(#blkFill)" />
            <path
                d={`M${toPath(blocked).split(" ").join(" L")}`}
                fill="none"
                stroke="#ef4444"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
            {/* Legend */}
            <rect x="10" y="2" width="8" height="8" rx="2" fill="#3b82f6" opacity="0.6" />
            <text x="22" y="9" className="fill-zinc-400" fontSize="7">
                Delivered
            </text>
            <rect x="80" y="2" width="8" height="8" rx="2" fill="#ef4444" opacity="0.6" />
            <text x="92" y="9" className="fill-zinc-400" fontSize="7">
                Blocked
            </text>
            {/* x-axis */}
            {[0, 6, 12, 18, 23].map((hr) => (
                <text
                    key={hr}
                    x={(hr / 23) * w}
                    y={h + 14}
                    textAnchor="middle"
                    className="fill-zinc-400"
                    fontSize="7"
                >{`${hr}:00`}</text>
            ))}
        </svg>
    );
}

/* ── DLP Section ──────────────────────────────────────────────────── */
function DlpSection() {
    const rules = [
        {
            rule: "Credit Card Numbers Detected",
            sensitivity: "High",
            action: "Blocked",
            matches: 14,
            color: "red" as const,
        },
        {
            rule: "PII in External Email",
            sensitivity: "High",
            action: "Quarantined",
            matches: 8,
            color: "orange" as const,
        },
        {
            rule: "Confidential Document Attached",
            sensitivity: "Medium",
            action: "Flagged",
            matches: 23,
            color: "amber" as const,
        },
    ];
    return (
        <div className="space-y-3">
            {rules.map((r) => (
                <div
                    key={r.rule}
                    className="rounded-xl border border-zinc-200/30 bg-white/40 p-4 backdrop-blur-sm transition-all hover:bg-white/60 dark:border-zinc-700/30 dark:bg-zinc-800/40 dark:hover:bg-zinc-800/60"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{r.rule}</p>
                            <div className="mt-1 flex items-center gap-2">
                                <Badge color={r.color}>{r.action}</Badge>
                                <span className="text-[10px] text-zinc-400">Sensitivity: {r.sensitivity}</span>
                            </div>
                        </div>
                        <div className="flex flex-col items-end">
                            <span className="bg-gradient-to-b from-zinc-800 to-zinc-600 bg-clip-text text-xl font-extrabold text-transparent dark:from-white dark:to-zinc-400">
                                {r.matches}
                            </span>
                            <span className="text-[9px] text-zinc-400">matches today</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ── Main Dashboard ───────────────────────────────────────────────── */
export function MailDashboard({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
}) {
    const { t } = useTranslation();
    const mailEvents = UNIFIED_EVENTS.filter((e) => e.system === "mail");
    const catMap: Record<string, "spam" | "phishing" | "malware"> = {
        "MAIL-4821": "phishing",
        "MAIL-4822": "spam",
        "MAIL-4823": "phishing",
        "MAIL-4824": "malware",
        "MAIL-4825": "phishing",
    };
    const catColor = { spam: "zinc", phishing: "orange", malware: "red" } as const;
    const fromMap: Record<string, string> = {
        "MAIL-4821": "invoice@fak3-vendor.com",
        "MAIL-4822": "noreply@ch34p-meds.ru",
        "MAIL-4823": "support@micros0ft-alert.com",
        "MAIL-4824": "delivery@pack4ge-track.cn",
        "MAIL-4825": "hr@company-update.net",
    };
    const subjectMap: Record<string, string> = {
        "MAIL-4821": "Rechnung #INV-2847",
        "MAIL-4822": "Special Offer 90% OFF",
        "MAIL-4823": "Account Verification Required",
        "MAIL-4824": "DHL Sendungsverfolgung",
        "MAIL-4825": "Gehaltsanpassung Q2",
    };

    return (
        <>
            <Heading>{t("mail.title")}</Heading>

            <div className="grid grid-cols-3 items-center gap-4">
                <Card className="col-span-1 h-full">
                    <div className="group flex cursor-pointer flex-col rounded-xl border border-transparent p-4 transition-all hover:border-red-500 hover:bg-red-500/5">
                        <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-red-600 text-white shadow-sm">
                            {/* Das Ausrufezeichen-Icon passend zum Screenshot */}
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="3"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v3.75m0 3.75h.008v.008H12v-.008Z"
                                />
                            </svg>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold tracking-tight text-zinc-950 dark:text-white">10</span>
                            <span className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Malicious</span>
                        </div>
                        <p className="mt-1 text-sm font-medium text-zinc-500">9 senders → 8 mailboxes</p>
                    </div>
                </Card>
                <Card className="col-span-2 flex h-full flex-col justify-center">
                    <div className="mt-4">
                        <Sparkline data={[2134, 2567, 2891, 2345, 3012, 2678, 2847]} />
                        <div className="mt-2 flex justify-between text-xs text-zinc-400">
                            <span>Mon</span>
                            <span>Tue</span>
                            <span>Wed</span>
                            <span>Thu</span>
                            <span>Fri</span>
                            <span>Sat</span>
                            <span>Sun</span>
                        </div>
                    </div>
                </Card>
            </div>
            {/* ── Original 3-col grid ─────────────────────────────────── */}
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Top Attack Types (Malicious)</Subheading>
                    <div className="mt-4 flex justify-center">
                        <RingChartNext
                            label="2,847"
                            segments={[
                                { value: 28, color: "#004D40", label: "BEC/Fraud" },
                                { value: 21, color: "#00695C", label: "Credential Phishing" },
                                { value: 10, color: "#00796B", label: "Spam" },
                                { value: 6, color: "#00897B", label: "Malware/Ransomeware" },
                                { value: 2, color: "#009688", label: "Reconnaissance" },
                                { value: 1, color: "#26A69A", label: "Callback Phishing" },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Top Tactics & Techniques (Malicious)</Subheading>
                    <div className="mt-4 flex justify-center">
                        <RingChartNext
                            label="2,847"
                            segments={[
                                { value: 31, color: "#3B0764", label: "Social engineering" },
                                { value: 12, color: "#7E22CE", label: "Evasion" },
                                { value: 8, color: "#6B21A8", label: "Imersonation: Employee" },
                                { value: 4, color: "#7E22CE", label: "Free email provider" },
                                { value: 1, color: "#9333EA", label: "HTML smuggling" },
                                { value: 1, color: "#A855F7", label: "Out of band pivot" },
                                { value: 1, color: "#C084FC", label: "Scripting" },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Cross-System Feed</Subheading>
                    <div className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
                        <FeedEntry
                            time="14:20"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="phishing"
                            onNavigate={onNavigate}
                        >
                            Phishing matches campaign <span className="font-medium">Q2 — IT Helpdesk</span>
                        </FeedEntry>
                        <FeedEntry
                            time="13:58"
                            icon={LockClosedIcon}
                            color="bg-blue-500"
                            linkedSystem="ztna"
                            onNavigate={onNavigate}
                        >
                            ZTNA flagged 3 compromised sessions
                        </FeedEntry>
                        <FeedEntry
                            time="13:45"
                            icon={ShieldCheckIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="soc"
                            onNavigate={onNavigate}
                        >
                            Malware quarantined — SOC Alert #1249
                        </FeedEntry>
                        <FeedEntry
                            time="13:45"
                            icon={SignalIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="ndr"
                            onNavigate={onNavigate}
                        >
                            C2 domain added to NDR blocklist
                        </FeedEntry>
                        <FeedEntry
                            time="13:31"
                            icon={BugAntIcon}
                            color="bg-blue-500 dark:bg-blue-400"
                            linkedSystem="pentests"
                            onNavigate={onNavigate}
                        >
                            Payload exploits same CVE as PT-004
                        </FeedEntry>
                    </div>
                </Card>
                <Card>
                    <div className="flex flex-col gap-10 py-4">
                        {/* 1. Spam - Orange Hover */}
                        <div className="group flex cursor-pointer flex-col rounded-xl border border-transparent p-4 transition-all hover:border-orange-500 hover:bg-orange-500/5">
                            <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500 text-white shadow-sm">
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                    />
                                </svg>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold tracking-tight text-zinc-950 dark:text-white">
                                    2
                                </span>
                                <span className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Spam</span>
                            </div>
                            <p className="mt-1 text-sm font-medium text-zinc-500">2 senders → 2 mailboxes</p>
                        </div>

                        {/* 2. Graymail - Gray/Zinc Hover */}
                        <div className="group flex cursor-pointer flex-col rounded-xl border border-transparent p-4 transition-all hover:border-zinc-500 hover:bg-zinc-500/10">
                            <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-700 text-white shadow-sm">
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2.5"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold tracking-tight text-zinc-950 dark:text-white">
                                    1
                                </span>
                                <span className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
                                    Graymail
                                </span>
                            </div>
                            <p className="mt-1 text-sm font-medium text-zinc-500">1 sender → 1 mailbox</p>
                        </div>

                        {/* 3. Email Bombs - Blue Hover */}
                        <div className="group flex cursor-pointer flex-col rounded-xl border border-transparent p-4 transition-all hover:border-blue-500 hover:bg-blue-500/5">
                            <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                                    />
                                </svg>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold tracking-tight text-zinc-950 dark:text-white">
                                    0
                                </span>
                                <span className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
                                    Email Bombs
                                </span>
                            </div>
                            <p className="mt-1 text-sm font-medium text-zinc-500">0 messages → 0 mailboxes</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* ── Email Flow Sankey ────────────────────────────────────── */}
            <SectionTitle title="Email Flow" description="Inbound email processing pipeline" />
            <Card>
                <EmailFlowSankey />
            </Card>

            {/* ── Sender Reputation + Attachment Analysis ─────────────── */}
            <SectionTitle
                title="Recipients & Senders"
                description="Breakdown of top targets of in your organization and the most common external senders. Based on malicious classifications, Attack Score verdicts, or ASA verdicts."
            />
            <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Top Targets</Subheading>
                    <div className="mt-4">
                        <TopTargets />
                    </div>
                </Card>
                <Card>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: "600px",
                        }}
                    >
                        <div>
                            <Subheading>Top Attacking Sender Domains</Subheading>
                            <div className="mt-4">
                                <TopSenderDomains />
                            </div>
                        </div>
                        <div>
                            <Subheading>Top Attacking Sender Emails</Subheading>
                            <div className="mt-4">
                                <TopAttackingSender />
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* ── Delivery Timeline ───────────────────────────────────── */}
            <SectionTitle title="Delivery Timeline (24h)" description="Email volume with blocked overlay" />
            <Card>
                <DeliveryTimeline />
            </Card>

            {/* ── Recent Blocked Table (original) ─────────────────────── */}
            <Subheading className="mt-10">{t("mail.recentBlocked")}</Subheading>
            <Table className="mt-4" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("mail.time")}</TableHeader>
                        <TableHeader>{t("mail.from")}</TableHeader>
                        <TableHeader>{t("mail.subject")}</TableHeader>
                        <TableHeader>{t("mail.category")}</TableHeader>
                        <TableHeader>{t("mail.action")}</TableHeader>
                        <TableHeader>Correlated</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {mailEvents.map((e) => (
                        <TableRow key={e.id}>
                            <TableCell className="font-mono text-zinc-500">{e.time}</TableCell>
                            <TableCell className="font-mono text-xs">{fromMap[e.id] ?? "—"}</TableCell>
                            <TableCell>{subjectMap[e.id] ?? e.title}</TableCell>
                            <TableCell>
                                <Badge color={catColor[catMap[e.id] ?? "spam"]}>
                                    {t(`mail.${catMap[e.id] ?? "spam"}`)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge color="red">{t("mail.rejected")}</Badge>
                            </TableCell>
                            <TableCell>
                                <CorrelationBadges systems={e.correlated} onNavigate={onNavigate} />
                            </TableCell>
                            <TableCell>
                                <button onClick={() => onInvestigate(e)} className="text-zinc-400 hover:text-blue-600">
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}
